import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { productionApi } from 'api/production';
import DateWeekInput from 'components/storefront/components/DateWeekInput';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Label } from 'shadcn/components/ui/Label';
import { MultiSelect } from 'shadcn/components/ui/MultiSelect';

import { usePackGroups } from './hooks/usePackGroups';

/**
 * Dialog for duplicating carousels.
 *
 * @param {Array} carousels - The list of carousels to duplicate.
 * @param {Date} initialDate - The initial date to use for the date picker.
 * @param {boolean} isOpen - Whether the dialog is open.
 * @param {Function} onClose - Function to close the dialog.
 */
export default function DuplicateCarouselsDialog({ carousels, initialDate, isOpen, onClose }) {
    const [date, setDate] = useState(initialDate ?? new Date());
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [selectedCarousels, setSelectedCarousels] = useState([]);
    const [selectedPackGroupIDs, setSelectedPackGroupIDs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { packGroups } = usePackGroups(date);

    const dialogRef = useRef(null);
    const multiSelectRef = useRef(null);

    const packGroupOptions = useMemo(
        () =>
            packGroups
                ?.filter((packGroup) => packGroup.name.split(' ').length === 1)
                .map((packGroup) => ({
                    label: packGroup.name,
                    value: packGroup.id,
                })) ?? [],
        [packGroups],
    );

    useEffect(() => {
        setSelectedCarousels(carousels?.map((carousel) => carousel.id) ?? []);
    }, [carousels]);

    const handleCheckedChange = (checked, carouselId) => {
        if (checked) {
            setSelectedCarousels([...selectedCarousels, carouselId]);
        } else {
            setSelectedCarousels(selectedCarousels.filter((id) => id !== carouselId));
        }
    };

    const handleSelectAllClick = () => {
        setSelectedCarousels(
            selectedCarousels.length === carousels?.length ? [] : (carousels?.map((carousel) => carousel.id) ?? []),
        );
    };

    const handleSelectDate = (newDate) => {
        setDate(newDate);
        setDatePopoverOpen(false);
    };

    const handleDuplicate = async () => {
        setIsLoading(true);
        const toastID = toast.loading('Duplicating carousels...');

        try {
            await Promise.all(
                selectedPackGroupIDs.map(async (packGroupID) => {
                    const itemsToRemap = carousels
                        .filter((carousel) => selectedCarousels.includes(carousel.id))
                        .reduce(
                            (acc, carousel) => ({
                                ...acc,
                                [carousel.id]: carousel.items.map((item) =>
                                    item.sellableItemID ? item.sellableItemID : item.id,
                                ),
                            }),
                            {},
                        );
                    const convertedResponse = await productionApi.getConvertedSellableItems(packGroupID, itemsToRemap);

                    await productionApi.createUpdateCarouselV2({
                        packGroupID,
                        carousels: carousels
                            .filter((carousel) => selectedCarousels.includes(carousel.id))
                            .map((carousel) => {
                                const carouselItems =
                                    convertedResponse?.data?.carousels[carousel.id] ??
                                    carousel.items.map((item) => ({
                                        ...item,
                                        id: item.sellableItemID ? item.sellableItemID : item.id,
                                    }));

                                return {
                                    ...carousel,
                                    id: null,
                                    packGroupID,
                                    // * The GET /carousels endpoint returns `linkTitle`, whereas the PUT/POST endpoints expect `linkText`.
                                    // * The GET /carousels endpoint returns `photoUrlMedium`, whereas the PUT/POST endpoints expect `imageUrl`.
                                    // * The GET /carousels endpoint returns items with the id set to `carousel_sellable_item_map.id`,
                                    //   whereas the PUT/POST endpoints expect the id to be the `sellable_item.id`.
                                    imageUrl: carousel.photoUrlMedium,
                                    linkText: carousel.linkTitle,
                                    items: carouselItems,
                                    rankNumber: null,
                                };
                            }),
                    });
                }),
            );
            onClose();
            toast.success('Carousels duplicated successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to duplicate carousels');
        } finally {
            setIsLoading(false);
            toast.dismiss(toastID);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent ref={dialogRef} className="flex flex-col sm:max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle>Duplicate Carousels</DialogTitle>
                    <DialogDescription>
                        This action will create new carousels with the same content within the specified pack group.
                        <br />
                        <br />
                        <span className="font-bold">
                            The carousels&apos; ranks will be cleared during the duplication process.
                        </span>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col h-full border rounded-md overflow-hidden">
                    <div className="text-sm font-medium border-b p-2 flex items-center justify-between">
                        Carousels
                        <Button variant="outline" size="sm" onClick={handleSelectAllClick}>
                            {selectedCarousels.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </div>
                    <div className="flex flex-col gap-3 p-2 h-full overflow-y-auto">
                        {carousels?.map((carousel) => (
                            <div key={carousel.id} className="flex items-center gap-3">
                                <Checkbox
                                    id={`duplicate-carousel-${carousel.id}`}
                                    checked={selectedCarousels.includes(carousel.id)}
                                    onCheckedChange={(checked) => handleCheckedChange(checked, carousel.id)}
                                />
                                <Label
                                    htmlFor={`duplicate-carousel-${carousel.id}`}
                                    className="w-full font-normal cursor-pointer"
                                >
                                    <span className="text-muted-foreground">{carousel.id}</span> {carousel.name}
                                </Label>
                            </div>
                        ))}
                    </div>
                </div>
                <DateWeekInput
                    date={date}
                    onSelectDate={handleSelectDate}
                    popoverOpen={datePopoverOpen}
                    setPopoverOpen={setDatePopoverOpen}
                />

                <div className="flex flex-col gap-2">
                    <Label htmlFor="duplicate-pack-groups">Pack Groups</Label>
                    <MultiSelect
                        ref={multiSelectRef}
                        id="duplicate-pack-groups"
                        maxCount={Infinity}
                        modalPopover
                        options={packGroupOptions}
                        placeholder="None"
                        onValueChange={(value) => setSelectedPackGroupIDs(value)}
                        defaultValue={[]}
                        container={dialogRef.current}
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="default" onClick={handleDuplicate} disabled={isLoading}>
                        Confirm
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
